// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * V08b: cross-origin one-click ownership signing.
 *
 * Entry point for the Cobault Verifier SPA's "Sign at user-app" popup.
 * The verifier opens this page as a popup with query params
 *   ?vault={uuid}&nonce={hex}&return_origin={https://…}
 * and listens for a `postMessage` carrying the DER-encoded ECDSA
 * signature.
 *
 * Security properties this page upholds:
 *   1. Params are hard-validated (UUID shape, 64 lowercase hex nonce,
 *      return_origin in a hard-coded per-network allow-list). No regex
 *      on the origin — exact string match only. No env-var widening of
 *      the trust boundary.
 *   2. No state mutation on user-app side. The page does not start a
 *      vault, does not persist anything, does not emit events to any
 *      other user-app component. Read the key, sign, post back, close.
 *   3. The postMessage reply targets the parsed + validated
 *      return_origin (never "*"), with a typed envelope echoing
 *      vault_id and nonce so the opener can correlate the reply to its
 *      original request.
 *   4. The signing primitive is the same `signOwnershipChallenge` RPC
 *      introduced in V06 — this page adds no new crypto.
 *
 * COB-507: the page now serves TWO sites, selected by the `flow` query
 * parameter, and every property above holds independently for each:
 *
 *   - absent / anything but the literal `shared` → the **yield** arm,
 *     byte-for-byte unchanged (yield allow-list, yield canonical message,
 *     `cobault-verifier-ownership-v1` / `-v2` envelopes). An unrecognised
 *     value deliberately falls back here rather than erroring: that is
 *     exactly what an older user-app does with the parameter, so the
 *     shared site's fail-closed story ("an old user-app signs the wrong
 *     message and the proof simply won't verify") stays true.
 *   - `flow=shared` → the **verifier-shared** arm: the shared canonical
 *     message (`cobault-verifier-shared-ownership-v1:` — a different domain
 *     separator, so the two proofs are never interchangeable), the separate
 *     verifier-shared origin allow-list, and the proof-object-only
 *     `cobault-verifier-shared-ownership-v1` envelope. verifier-shared's
 *     `/prove` accepts no bare-DER envelope, so this arm never posts one.
 *
 * The shared arm carries no client-side timeout of its own: the opener's
 * deadline is the server's nonce TTL (300 s), and hardware participants ride
 * this popup for their on-device ceremony.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { useRoute } from 'preact-iso';
import type { AppSignatureProof } from '@cobault/key-agent';
import { api } from '../api.js';
import { isAllowedSharedReturnOrigin } from '../verifier-shared-origins.js';
import { useSession } from '../session-context.js';
import { Onboarding } from './onboarding.js';
import {
  LoginMethodChooser,
  useWeb3AuthLogin,
} from '../components/login-method-chooser.js';
import { HardwareLogin } from '../components/hardware-login.js';
import { Web3AuthEmailCapture } from '../components/web3auth-email-capture.js';
import { Web3AuthLossAck } from '../components/web3auth-loss-ack.js';
import '../styles/sign-verifier-challenge.css';

// ── Allow-list ────────────────────────────────────────────────────────
//
// Hard-coded, per-network. A typo'd env var must not silently widen
// the trust boundary. When a mainnet verifier hostname lands, slot it
// in here explicitly — do not regex over a domain suffix.

const VERIFIER_ORIGINS_BY_NETWORK: Record<string, readonly string[]> = {
  // Dev: vite serves verifier SPA on :5174.
  regtest: ['http://localhost:5174'],
  // Public networks. M-series staging runs on signet at
  // `verifier.staging.cobault.io`; legacy/mainnet remains on
  // `app.verifier.cobault.io`. Both pin explicitly — never regex over
  // a domain suffix. If a distinct mainnet hostname lands later, slot
  // it in here.
  signet: [
    'https://verifier.staging.cobault.io',
    'https://app.verifier.cobault.io',
  ],
  testnet: ['https://app.verifier.cobault.io'],
  bitcoin: ['https://app.verifier.cobault.io'],
};

/**
 * True iff `origin` exactly matches one of the verifier origins permitted
 * for the given bitcoin network. Unknown networks return false (fail
 * closed).
 */
export function isAllowedReturnOrigin(origin: string, network: string): boolean {
  const allowed = VERIFIER_ORIGINS_BY_NETWORK[network];
  if (!allowed) return false;
  return allowed.includes(origin);
}

// ── Param parsing ─────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const NONCE_RE = /^[0-9a-f]{64}$/;

/** Which verifier site opened this popup. `yield` is the V08b default (the
 *  `cobault-verifier` SPA); `shared` is COB-507's verifier-shared review site.
 *  The two differ in canonical message, origin allow-list, and reply envelope
 *  — nothing else on this page. */
export type SignFlow = 'yield' | 'shared';

/** The pinned `flow` query value that selects the shared arm. Matched as an
 *  exact literal: only this exact string activates it. */
export const SHARED_FLOW_PARAM = 'shared';

/**
 * Resolve the `flow` query parameter. Only the exact literal `shared` selects
 * the shared arm; absent, empty, and unrecognised values all resolve to the
 * yield arm, which is the byte-for-byte pre-COB-507 behaviour (and exactly
 * what a user-app that predates this parameter does with it).
 *
 * Exported for unit tests.
 */
export function parseSignFlow(raw: string | undefined): SignFlow {
  return raw === SHARED_FLOW_PARAM ? 'shared' : 'yield';
}

export type ParsedParams =
  | {
      kind: 'ok';
      flow: SignFlow;
      vault: string;
      nonce: string;
      return_origin: string;
    }
  | { kind: 'invalid'; reason: string };

/**
 * Parse and validate the URL query params. Rejects anything that fails
 * syntactic validation; callers additionally gate `return_origin`
 * against the per-network allow-list before acting on the result.
 *
 * COB-507: the syntactic validation is identical for both flows — same UUID
 * shape, same 64-lowercase-hex nonce, same bare-origin requirement. Only the
 * allow-list the caller then applies differs.
 *
 * Exported for unit tests.
 */
export function parseSignParams(query: Record<string, string>): ParsedParams {
  const flow = parseSignFlow(query.flow);
  const vault = (query.vault ?? '').toLowerCase();
  const nonce = (query.nonce ?? '').toLowerCase();
  const return_origin = query.return_origin ?? '';

  if (!vault) return { kind: 'invalid', reason: 'Missing vault parameter.' };
  if (!UUID_RE.test(vault)) {
    return { kind: 'invalid', reason: 'Vault parameter is not a valid UUID.' };
  }
  if (!nonce) return { kind: 'invalid', reason: 'Missing nonce parameter.' };
  if (!NONCE_RE.test(nonce)) {
    return {
      kind: 'invalid',
      reason: 'Nonce must be exactly 64 lowercase hex characters.',
    };
  }
  if (!return_origin) {
    return { kind: 'invalid', reason: 'Missing return_origin parameter.' };
  }
  // Parse the origin to confirm it has a valid URL shape. The allow-list
  // check is the security-critical one; this is purely a shape guard so
  // we can render the origin in UI without worrying about malformed
  // strings.
  try {
    const u = new URL(return_origin);
    // Ensure the query param was the bare origin, not a full URL —
    // anything beyond origin (path, query, fragment) is rejected.
    if (u.origin !== return_origin) {
      return {
        kind: 'invalid',
        reason: 'return_origin must be a bare origin (scheme + host + port).',
      };
    }
  } catch {
    return { kind: 'invalid', reason: 'return_origin is not a valid URL.' };
  }

  return { kind: 'ok', flow, vault, nonce, return_origin };
}

/**
 * COB-507: the return-origin gate for a parsed request, dispatched on the
 * flow. Kept as one function so no call site can check the wrong allow-list:
 * the yield arm may only reply to a `cobault-verifier` origin, the shared arm
 * only to the single mapped `verifier-shared` origin for this network.
 */
export function isAllowedReturnOriginForFlow(
  flow: SignFlow,
  origin: string,
  network: string,
): boolean {
  return flow === 'shared'
    ? isAllowedSharedReturnOrigin(origin, network)
    : isAllowedReturnOrigin(origin, network);
}

// ── Reply payload ─────────────────────────────────────────────────────

/** Exact envelope posted back to the verifier SPA. The opener matches
 *  on `type` and cross-checks `vault_id` + `nonce` against its
 *  originating request. */
export interface OwnershipSignatureReply {
  type: 'cobault-verifier-ownership-v1';
  vault_id: string;
  nonce: string;
  signature_der_hex: string;
}

/** HW.11 (COB-331): the v2 envelope carrying a WS-H proof object instead of
 *  a bare DER hex. A hardware (Ledger) session emits a `bsm-bip137-v1` proof
 *  — a Ledger cannot produce the raw `der-sha256-v1` signature the v1
 *  envelope carries — so the popup posts this shape for `agentKind ===
 *  'hardware'`. The verifier's `requestSignatureFromUserApp` listens for
 *  both envelopes and routes a proof to `/prove-v2` (HW.7) and a DER to the
 *  legacy `/prove`. Software / Web3Auth sessions keep posting the v1
 *  envelope unchanged. */
export interface OwnershipProofReply {
  type: 'cobault-verifier-ownership-v2';
  vault_id: string;
  nonce: string;
  proof: AppSignatureProof;
}

/** COB-507: the ONLY envelope the `flow=shared` arm posts. Proof-object only —
 *  verifier-shared's `/prove` accepts no bare-DER form, so there is
 *  deliberately no v1/DER sibling for this site and no top-level
 *  `signature_der_hex` field. Both backends fill `proof`: software / Web3Auth
 *  with `der-sha256-v1`, a Ledger with `bsm-bip137-v1`. The `type` is distinct
 *  from the yield site's discriminators so a reply intended for one site is
 *  inert at the other. Mirrored from the verifier repo's
 *  `sign-via-user-app-shared.ts::SharedOwnershipProofReply` — keep in sync. */
export interface SharedOwnershipProofReply {
  type: 'cobault-verifier-shared-ownership-v1';
  vault_id: string;
  nonce: string;
  proof: AppSignatureProof;
}

/** COB-507: the shared arm's envelope `type` discriminator, pinned as a
 *  constant so the reply builder and its test read one source. */
export const SHARED_REPLY_TYPE = 'cobault-verifier-shared-ownership-v1';

// ── Canonical message preview ─────────────────────────────────────────

/** The exact bytes that the key-agent will sign. Mirrored here (as a
 *  preview string) from `signOwnershipMessage` in the key-agent crypto
 *  module so the confirmation card can show the user what they are
 *  authorising. This is for display only — the actual signature is
 *  computed inside the Worker over the same canonical string. */
export function canonicalOwnershipMessage(vaultId: string, nonceHex: string): string {
  return `cobault-verifier-ownership-v1:${vaultId}:${nonceHex}`;
}

/** COB-507: the shared site's canonical message preview — mirrored from
 *  `sharedOwnershipChallengeMessage` in the key-agent crypto module. The
 *  `-shared-` infix is load-bearing: it is the whole reason a yield proof
 *  cannot be replayed at verifier-shared, and vice versa. Display only; the
 *  signature is computed inside the Worker over the same canonical string. */
export function canonicalSharedOwnershipMessage(
  vaultId: string,
  nonceHex: string,
): string {
  return `cobault-verifier-shared-ownership-v1:${vaultId}:${nonceHex}`;
}

/** The canonical message preview for whichever site opened this popup. */
export function canonicalMessageForFlow(
  flow: SignFlow,
  vaultId: string,
  nonceHex: string,
): string {
  return flow === 'shared'
    ? canonicalSharedOwnershipMessage(vaultId, nonceHex)
    : canonicalOwnershipMessage(vaultId, nonceHex);
}

// ── Shared-vault signing context (COB-507) ────────────────────────────

/** What the shared arm can learn about a vault before signing. Both fields
 *  are best-effort: an unauthenticated popup (or a failed fetch) signs with
 *  neither, falling back to the key-agent's local vault→index map. */
export interface SharedSigningContext {
  /** The vault's HUMAN roster, when `GET /shared-vault/{id}/activation`
   *  answered. Enables the strong, roster-proven index resolution. */
  roster?: readonly string[];
  /** The caller's server-known BIP44 index (`my_derivation_index`), when
   *  `GET /shared-vault/{id}` answered. Heals a missing local mapping. */
  derivationIndexHint?: number;
}

/**
 * Best-effort fetch of the shared-vault signing context.
 *
 * Neither call is required: this popup runs a FRESH session (the parent tab's
 * JWT is in-memory and does not cross the window boundary), and a participant
 * who logged in here purely to sign may not be authenticated to user-app at
 * all. Both failures are logged and swallowed — the key-agent then resolves
 * the index from its local map, and a wrong index costs a retry, never a coin
 * (verifier-shared exact-compares the recovered key against the roster).
 *
 * Exported for unit tests via the injectable client.
 */
export async function loadSharedVaultSigningContext(
  vaultId: string,
  isAuthenticated: boolean,
  status: string,
  client: {
    getSharedVault: typeof api.getSharedVault;
    getSharedVaultActivation: typeof api.getSharedVaultActivation;
  } = api,
): Promise<SharedSigningContext> {
  if (!isAuthenticated) {
    console.warn(
      '[sign-verifier-challenge] session.isAuthenticated=false; signing the ' +
        'shared challenge from the local vault→index map only. status=',
      status,
    );
    return {};
  }

  const context: SharedSigningContext = {};
  try {
    const details = await client.getSharedVault(vaultId);
    if (typeof details.my_derivation_index === 'number') {
      context.derivationIndexHint = details.my_derivation_index;
    } else {
      console.warn(
        '[sign-verifier-challenge] getSharedVault returned no my_derivation_index',
        { vault: vaultId },
      );
    }
  } catch (err) {
    console.warn('[sign-verifier-challenge] getSharedVault failed:', err);
  }

  try {
    // 409s before the vault is sealed (PROPOSED onwards has a roster); that is
    // a normal, expected miss, not an error worth surfacing to the user.
    const bundle = await client.getSharedVaultActivation(vaultId);
    if (Array.isArray(bundle.roster) && bundle.roster.length > 0) {
      context.roster = bundle.roster;
    }
  } catch (err) {
    console.warn(
      '[sign-verifier-challenge] getSharedVaultActivation failed; falling back ' +
        'to hint/local-map index resolution:',
      err,
    );
  }

  return context;
}

// ── Page component ────────────────────────────────────────────────────

type SignState =
  | { kind: 'idle' }
  | { kind: 'signing' }
  | { kind: 'signed' }
  | { kind: 'error'; message: string };

export function SignVerifierChallenge() {
  const session = useSession();
  const { query } = useRoute();

  const parsed = useMemo(() => parseSignParams(query), [query]);

  // HW.11: the popup runs a FRESH session (the parent-tab JWT is in-memory and
  // does not cross the window boundary), so it must offer the full login
  // chooser itself — including the hardware (Ledger) path — exactly as the home
  // page does. Without this the enabled verifier "Hardware wallet" tile would
  // dead-end at a popup with no device login. The chooser is only rendered in
  // the logged-out branch below, but the hook must run before any early return.
  const {
    loginMethod,
    setLoginMethod,
    web3authBusy,
    web3authError,
    collisionEmail,
    setCollisionEmail,
    handleWeb3Auth,
  } = useWeb3AuthLogin();

  // Wait for /version so we can pick the right allow-list.
  if (session.bitcoinNetwork === null) {
    return (
      <div class="sign-challenge-shell">
        <p class="sign-challenge-loading">Loading…</p>
      </div>
    );
  }

  if (parsed.kind === 'invalid') {
    return (
      <div class="sign-challenge-shell">
        <ErrorCard
          title="Invalid signing request"
          detail={parsed.reason}
          showClose
        />
      </div>
    );
  }

  if (
    !isAllowedReturnOriginForFlow(
      parsed.flow,
      parsed.return_origin,
      session.bitcoinNetwork,
    )
  ) {
    return (
      <div class="sign-challenge-shell">
        <ErrorCard
          title="Return origin not permitted"
          detail={
            `The requesting site (${parsed.return_origin}) is not an ` +
            `approved Cobault Verifier origin for the ${session.bitcoinNetwork} ` +
            `network. Nothing has been signed.`
          }
          showClose
        />
      </div>
    );
  }

  // COB-345: a provider-less Web3Auth sign-in commits the agent to session
  // state but is NOT yet authenticated — it needs an email captured before it
  // can register. This page gates the confirm card on `session.agent`, which
  // is already set during that window, so the capture panel must win over
  // ConfirmAndSign here; once the register lands, `isAuthenticated` flips and
  // the confirm card takes over below.
  if (loginMethod === 'web3auth-email' && !session.isAuthenticated) {
    return (
      <div class="sign-challenge-shell">
        <Web3AuthEmailCapture onBack={() => setLoginMethod(null)} />
      </div>
    );
  }

  // COB-272: login-loss acknowledgement gating a new provider-email Web3Auth
  // account's deferred register — same early-return shape as the capture
  // panel above (it must win over ConfirmAndSign while the register is
  // pending). A collision bounces to Self Custody with the email prefilled.
  if (loginMethod === 'web3auth-ack' && !session.isAuthenticated) {
    return (
      <div class="sign-challenge-shell">
        <Web3AuthLossAck
          onBack={() => setLoginMethod(null)}
          onCollision={(email) => {
            setCollisionEmail(email);
            setLoginMethod('local');
          }}
        />
      </div>
    );
  }

  // User has no session — render the login chooser inline (same methods as the
  // home page: Cobault self-custody, Web3Auth, or a Ledger). Once auth
  // completes the SessionProvider re-renders us with session.agent set, and the
  // confirm card appears below. A hardware login sets agentKind='hardware', so
  // ConfirmAndSign then produces a bsm-bip137-v1 proof.
  if (!session.agent) {
    return (
      <div class="sign-challenge-shell">
        <div class="sign-challenge-prelude">
          <h1 class="sign-challenge-title">Cobault Verifier wants a signature</h1>
          <p class="sign-challenge-help">
            Log in to Cobault to prove you{' '}
            {parsed.flow === 'shared' ? 'hold a key in' : 'control'} vault{' '}
            <code class="mono">{parsed.vault.slice(0, 8)}…</code>. After you
            sign in, you'll see a confirmation card with the exact challenge
            before any signature is sent.
          </p>
        </div>
        {loginMethod === null && (
          <LoginMethodChooser
            onSelect={setLoginMethod}
            onWeb3Auth={handleWeb3Auth}
            web3authBusy={web3authBusy}
            web3authError={web3authError}
          />
        )}
        {loginMethod === 'local' && (
          <>
            {collisionEmail && (
              <p class="login-collision-notice">
                You already have a Cobault account with{' '}
                <strong>{collisionEmail}</strong>. Sign in with the method you
                used when you created it — if that was a passcode, enter it
                below.
              </p>
            )}
            <Onboarding
              onBack={() => {
                setLoginMethod(null);
                setCollisionEmail(null);
              }}
              initialEmail={collisionEmail ?? undefined}
            />
          </>
        )}
        {loginMethod === 'hardware' && (
          <HardwareLogin onBack={() => setLoginMethod(null)} />
        )}
      </div>
    );
  }

  return (
    <div class="sign-challenge-shell">
      <ConfirmAndSign
        flow={parsed.flow}
        vault={parsed.vault}
        nonce={parsed.nonce}
        returnOrigin={parsed.return_origin}
      />
    </div>
  );
}

function ConfirmAndSign({
  flow,
  vault,
  nonce,
  returnOrigin,
}: {
  flow: SignFlow;
  vault: string;
  nonce: string;
  returnOrigin: string;
}) {
  const session = useSession();
  const [state, setState] = useState<SignState>({ kind: 'idle' });

  const canonical = useMemo(
    () => canonicalMessageForFlow(flow, vault, nonce),
    [flow, vault, nonce],
  );

  // Auto-close after successful sign so the verifier tab takes over.
  useEffect(() => {
    if (state.kind !== 'signed') return;
    const timer = setTimeout(() => {
      // In a popup, window.close() works without a user gesture because
      // the window was opened programmatically by its opener. Wrap in
      // try/catch so a user who navigated here directly (not via popup)
      // doesn't see a console error.
      try {
        window.close();
      } catch {
        /* ignore */
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [state.kind]);

  const onSign = useCallback(async () => {
    if (!session.agent) return;
    setState({ kind: 'signing' });
    try {
      const unlocked = await session.agent.isUnlocked();
      if (!unlocked) {
        setState({
          kind: 'error',
          message:
            'Your Cobault session is locked. Unlock it in the main Cobault tab, then click Sign again.',
        });
        return;
      }

      // COB-507: the shared arm reads the SHARED vault's own hint + roster.
      // `getVaultDetails` is the yield-vault route and would 404 here, and the
      // roster — when we can get it — buys the strong, roster-proven index
      // resolution the acknowledge / co-sign paths use.
      if (flow === 'shared') {
        const { roster, derivationIndexHint } = await loadSharedVaultSigningContext(
          vault,
          session.isAuthenticated,
          session.status,
        );
        const proof = await session.agent.signSharedVaultOwnershipProof(
          vault,
          nonce,
          roster,
          derivationIndexHint,
        );
        // Proof object only — verifier-shared has no bare-DER envelope, so a
        // hardware (`bsm-bip137-v1`) and a software (`der-sha256-v1`) session
        // post the identical shape and the site routes on `proof.format`.
        const reply: SharedOwnershipProofReply = {
          type: SHARED_REPLY_TYPE,
          vault_id: vault,
          nonce,
          proof,
        };
        if (window.opener) {
          window.opener.postMessage(reply, returnOrigin);
        }
        setState({ kind: 'signed' });
        return;
      }

      // V08b fix: best-effort fetch the vault's server-known
      // derivation_index so we can heal a missing local IndexedDB
      // mapping. Common cause: the user restored their seed (bringing
      // back the mnemonic without the per-vault indices) or the vault
      // was created in a different browser profile. The fetch is only
      // attempted when the session is authenticated; if it fails (JWT
      // expired, rate limited, etc.) we fall through and let the
      // key-agent try with whatever mapping it already has.
      let derivationIndexHint: number | undefined;
      if (session.isAuthenticated) {
        try {
          const details = await api.getVaultDetails(vault);
          if (typeof details.derivation_index === 'number') {
            derivationIndexHint = details.derivation_index;
          } else {
            console.warn(
              '[sign-verifier-challenge] getVaultDetails returned no derivation_index',
              { vault, details },
            );
          }
        } catch (err) {
          // Best-effort — fall through to let key-agent try with whatever
          // local mapping it has. Surface the failure in the console so
          // devtools makes the cause obvious next time this error fires.
          console.warn(
            '[sign-verifier-challenge] getVaultDetails failed:',
            err,
          );
        }
      } else {
        console.warn(
          '[sign-verifier-challenge] session.isAuthenticated=false; skipping getVaultDetails. status=',
          session.status,
        );
      }

      // HW.11: a Ledger cannot emit the raw `der-sha256-v1` signature the v1
      // envelope carries, so a hardware session signs a `bsm-bip137-v1` proof
      // on-device and posts the v2 envelope; the verifier routes it to
      // `/prove-v2`. Software / Web3Auth keep the byte-identical v1 DER path.
      if (session.agentKind === 'hardware') {
        const proof = await session.agent.signOwnershipChallengeProof(
          vault,
          nonce,
          derivationIndexHint,
        );
        const reply: OwnershipProofReply = {
          type: 'cobault-verifier-ownership-v2',
          vault_id: vault,
          nonce,
          proof,
        };
        if (window.opener) {
          window.opener.postMessage(reply, returnOrigin);
        }
        setState({ kind: 'signed' });
        return;
      }

      const signature_der_hex = await session.agent.signOwnershipChallenge(
        vault,
        nonce,
        derivationIndexHint,
      );
      const reply: OwnershipSignatureReply = {
        type: 'cobault-verifier-ownership-v1',
        vault_id: vault,
        nonce,
        signature_der_hex,
      };
      if (window.opener) {
        window.opener.postMessage(reply, returnOrigin);
      }
      setState({ kind: 'signed' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Known friendly-cases:
      //   - "No derivation index found for vault X": the server didn't
      //     hand us a derivation_index (either the API call failed or
      //     the vault isn't owned by this account). Suggest restoring
      //     or signing in with the right account.
      //   - SessionExpiredError: the agent auto-locked between page
      //     load and click.
      if (message.includes('No derivation index found')) {
        setState({
          kind: 'error',
          message:
            "This device doesn't have a key for that vault, and we couldn't reach the server to recover the mapping. Sign in with the account that owns this vault, or restore your seed on this device.",
        });
        return;
      }
      setState({ kind: 'error', message });
    }
  }, [
    session.agent,
    session.agentKind,
    session.isAuthenticated,
    session.status,
    flow,
    vault,
    nonce,
    returnOrigin,
  ]);

  const onCancel = useCallback(() => {
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }, []);

  if (state.kind === 'signed') {
    return (
      <div class="sign-challenge-card sign-challenge-card--success">
        <h1 class="sign-challenge-title">Signed</h1>
        <p class="sign-challenge-help">
          Sent the signature back to Cobault Verifier. You can close this
          window.
        </p>
      </div>
    );
  }

  return (
    <div class="sign-challenge-card">
      <div class="sign-challenge-eyebrow">
        <span class="sign-challenge-diamonds" aria-hidden="true">◇◆◇</span>
        You sign with your key
      </div>
      <h1 class="sign-challenge-title">
        {flow === 'shared' ? 'Prove you are a participant' : 'Prove vault ownership'}
      </h1>
      {flow === 'shared' ? (
        <p class="sign-challenge-intro">
          <strong>Cobault Verifier</strong> — the independent site at{' '}
          <code class="mono">{returnOrigin}</code> — is asking you to prove you
          hold a key in this shared vault, so it can show you the policy it
          holds. Signing tells the verifier that you are a participant in vault{' '}
          <code class="mono">{vault}</code>, nothing more. No coins move and no
          Cobault state is changed.
        </p>
      ) : (
        <p class="sign-challenge-intro">
          <strong>Cobault Verifier</strong> — the independent site at{' '}
          <code class="mono">{returnOrigin}</code> — is asking you to prove
          you control this vault. Signing tells the verifier that you own
          vault{' '}
          <code class="mono">{vault}</code>, nothing more. No coins move
          and no Cobault state is changed.
        </p>
      )}

      <div class="sign-challenge-field">
        <div class="sign-challenge-field-label">Vault</div>
        <div class="sign-challenge-field-value mono">{vault}</div>
      </div>

      <div class="sign-challenge-field">
        <div class="sign-challenge-field-label">
          Canonical message (signed verbatim)
        </div>
        <code class="sign-challenge-canonical">{canonical}</code>
      </div>

      {state.kind === 'error' && (
        <div class="sign-challenge-error" role="alert">
          {state.message}
        </div>
      )}

      <div class="sign-challenge-actions">
        <button
          type="button"
          class="btn btn-secondary"
          onClick={onCancel}
          disabled={state.kind === 'signing'}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={onSign}
          disabled={state.kind === 'signing'}
        >
          {state.kind === 'signing' ? 'Signing…' : 'Sign'}
        </button>
      </div>
    </div>
  );
}

function ErrorCard({
  title,
  detail,
  showClose,
}: {
  title: string;
  detail: string;
  showClose: boolean;
}) {
  return (
    <div class="sign-challenge-card sign-challenge-card--error">
      <h1 class="sign-challenge-title">{title}</h1>
      <p class="sign-challenge-help">{detail}</p>
      {showClose && (
        <div class="sign-challenge-actions sign-challenge-actions--single">
          <button
            type="button"
            class="btn btn-secondary"
            onClick={() => {
              try {
                window.close();
              } catch {
                /* ignore */
              }
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
